import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Clock } from 'lucide-react';

const BRANCHES = [
  { id: 2, area: 'Тракторозаводский р-н (ЧТЗ)', address: 'ул. Бажова, д.48', phone: '+7 (351) 750-06-36', hours: '9:00-20:00', coords: [55.182326, 61.450164] },
  { id: 4, area: 'Тракторозаводский р-н (ЧТЗ)', address: 'ул. Мамина, д.21', phone: '+7 919 123 26 36', hours: '9:00-20:00', coords: [55.191048, 61.483312] },
  { id: 5, area: 'Ленинский р-н', address: 'ул. Гагарина, д.7', phone: '+7 (351) 223-26-36', hours: '9:00-20:00', coords: [55.142952, 61.444586] },
  { id: 6, area: 'Советский р-н', address: 'ул. Блюхера, д.91', phone: '+7 (351) 750-06-36', hours: '9:00-20:00', coords: [55.121463, 61.364312] },
];

const LocationsSection: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const placemarksRef = useRef<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    // Load Yandex Maps API
    const script = document.createElement('script');
    script.src = 'https://api-maps.yandex.ru/2.1/?apikey=&lang=ru_RU';
    script.async = true;
    script.onload = () => {
      // @ts-ignore
      if (window.ymaps) {
        // @ts-ignore
        ymaps.ready(() => {
          if (mapContainerRef.current) {
            // @ts-ignore
            const map = new ymaps.Map(mapContainerRef.current, {
              center: [55.1644, 61.4012], // Center of Chelyabinsk
              zoom: 12,
              controls: ['zoomControl', 'fullscreenControl']
            });

            mapRef.current = map;

            // Add placemarks for each branch
            BRANCHES.forEach((branch, index) => {
              // @ts-ignore
              const placemark = new ymaps.Placemark(
                branch.coords,
                {
                  balloonContentHeader: `<strong style="color: #f97316; font-size: 16px;">${branch.area}</strong>`,
                  balloonContentBody: `
                    <div style="padding: 8px;">
                      <p style="margin: 8px 0; font-size: 14px;">${branch.address}</p>
                      <p style="margin: 8px 0; font-size: 14px; color: #f97316; font-weight: bold;">${branch.phone}</p>
                      <p style="margin: 8px 0; font-size: 13px; color: #666;">${branch.hours}</p>
                    </div>
                  `,
                  hintContent: branch.area
                },
                {
                  preset: 'islands#orangeIcon',
                  iconColor: '#f97316'
                }
              );
              map.geoObjects.add(placemark);
              placemarksRef.current[index] = placemark;
            });
            
            // Mark map as ready
            setMapReady(true);
          }
        });
      }
    };
    
    // Check if script already exists
    const existingScript = document.querySelector('script[src*="api-maps.yandex.ru"]');
    if (!existingScript) {
      document.head.appendChild(script);
    } else {
      // Script already loaded, just initialize
      // @ts-ignore
      if (window.ymaps) {
        // @ts-ignore
        ymaps.ready(() => {
          if (mapContainerRef.current && !mapRef.current) {
            // @ts-ignore
            const map = new ymaps.Map(mapContainerRef.current, {
              center: [55.1644, 61.4012],
              zoom: 12,
              controls: ['zoomControl', 'fullscreenControl']
            });

            mapRef.current = map;

            BRANCHES.forEach((branch, index) => {
              // @ts-ignore
              const placemark = new ymaps.Placemark(
                branch.coords,
                {
                  balloonContentHeader: `<strong style="color: #f97316; font-size: 16px;">${branch.area}</strong>`,
                  balloonContentBody: `
                    <div style="padding: 8px;">
                      <p style="margin: 8px 0; font-size: 14px;">${branch.address}</p>
                      <p style="margin: 8px 0; font-size: 14px; color: #f97316; font-weight: bold;">${branch.phone}</p>
                      <p style="margin: 8px 0; font-size: 13px; color: #666;">${branch.hours}</p>
                    </div>
                  `,
                  hintContent: branch.area
                },
                {
                  preset: 'islands#orangeIcon',
                  iconColor: '#f97316'
                }
              );
              map.geoObjects.add(placemark);
              placemarksRef.current[index] = placemark;
            });
            
            setMapReady(true);
          }
        });
      }
    }

    return () => {
      // Cleanup map instance
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
  }, []);

  const handleBranchClick = (branchId: number) => {
    setSelectedBranch(branchId);
    const branchIndex = BRANCHES.findIndex(b => b.id === branchId);
    
    if (!mapReady) {
      return;
    }
    
    if (branchIndex !== -1 && mapRef.current && placemarksRef.current[branchIndex]) {
      const branch = BRANCHES[branchIndex];
      const placemark = placemarksRef.current[branchIndex];
      
      try {
        // Use setCenter with checkZoomRange option for better centering
        mapRef.current.setCenter(branch.coords, 14, {
          checkZoomRange: true,
          duration: 600
        });
        
        // Open balloon after a short delay
        setTimeout(() => {
          if (placemark && placemark.balloon) {
            placemark.balloon.open();
          }
        }, 700);
      } catch (error) {
        console.error('Error centering map:', error);
      }
    }
  };

  return (
    <section id="locations" className="relative z-10 py-12 bg-[#0b1120] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 md:mb-16 px-4 border-l-4 border-[#f97316] pl-6">
          <h2 className="text-4xl md:text-7xl font-heading font-bold uppercase leading-[0.9] break-words w-full md:w-auto text-white">
            <span className="text-[#94a3b8]">Наши</span> <br/> 
            Филиалы
          </h2>
          <p className="text-gray-400 max-w-md text-right mt-4 md:mt-0 font-mono text-sm uppercase">
            4 филиала по всему городу
          </p>
        </div>

        <div 
          ref={mapContainerRef}
          className="mb-12 rounded overflow-hidden border border-white/10 h-[450px]"
          style={{ width: '100%' }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {BRANCHES.map((branch) => (
            <div
              key={branch.id}
              onClick={() => handleBranchClick(branch.id)}
              className={`bg-[#1e293b]/50 p-6 border transition-all cursor-pointer relative ${
                selectedBranch === branch.id 
                  ? 'border-[#f97316] shadow-lg shadow-[#f97316]/20' 
                  : 'border-white/5 hover:border-[#f97316]'
              } group`}
            >
              {/* Click indicator */}
              <div className="absolute top-4 right-4 text-gray-400 group-hover:text-[#f97316] transition-colors">
                <svg className="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-full transition-colors ${
                  selectedBranch === branch.id
                    ? 'bg-[#f97316] text-black'
                    : 'bg-[#f97316]/10 text-[#f97316] group-hover:bg-[#f97316] group-hover:text-black'
                }`}>
                  <MapPin className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold font-heading">{branch.area}</h3>
              </div>
              <p className="text-gray-300 mb-3 text-sm leading-relaxed">{branch.address}</p>
              <div className="flex items-center gap-2 text-[#f97316] font-mono font-bold mb-2">
                <Phone className="w-4 h-4" />
                <span>{branch.phone}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Clock className="w-4 h-4" />
                <span>{branch.hours}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
